import { useMemo, useState } from 'react';
import { cn } from '../lib/utils';

export type SortDirection = 'asc' | 'desc';

export type SortState = {
  key: string;
  direction: SortDirection;
};

export function useTableSort(
  defaultKey: string,
  defaultDirection: SortDirection = 'asc',
): {
  sort: SortState;
  toggleSort: (key: string) => void;
  setSort: React.Dispatch<React.SetStateAction<SortState>>;
} {
  const [sort, setSort] = useState<SortState>({
    key: defaultKey,
    direction: defaultDirection,
  });

  function toggleSort(key: string) {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  }

  return { sort, toggleSort, setSort };
}

type SortableValue = string | number | boolean | null | undefined;

function normalizeSortValue(value: SortableValue): string | number {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const trimmed = String(value).trim();
  if (trimmed === '') return '';
  // Decimal money strings / numeric text
  const asNumber = Number(trimmed.replace(',', '.'));
  if (!Number.isNaN(asNumber) && /^-?\d+([.,]\d+)?$/.test(trimmed)) {
    return asNumber;
  }
  return trimmed.toLocaleLowerCase('el-GR');
}

export function compareSortValues(
  a: SortableValue,
  b: SortableValue,
  direction: SortDirection,
): number {
  const av = normalizeSortValue(a);
  const bv = normalizeSortValue(b);
  const dir = direction === 'asc' ? 1 : -1;
  if (av < bv) return -1 * dir;
  if (av > bv) return 1 * dir;
  return 0;
}

export function sortItems<T>(
  items: T[],
  sort: SortState,
  getters: Record<string, (item: T) => SortableValue>,
): T[] {
  const getter = getters[sort.key];
  if (!getter) return items;
  return [...items].sort((a, b) =>
    compareSortValues(getter(a), getter(b), sort.direction),
  );
}

/** Memoized client-side sort helper. */
export function useSortedItems<T>(
  items: T[],
  sort: SortState,
  getters: Record<string, (item: T) => SortableValue>,
): T[] {
  return useMemo(
    () => sortItems(items, sort, getters),
    [items, sort, getters],
  );
}

type SortableThProps = {
  sortKey: string;
  sort: SortState;
  onSort: (key: string) => void;
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** Allow the label to wrap inside the column width. */
  wrap?: boolean;
};

function SortIcon({
  active,
  direction,
}: {
  active: boolean;
  direction?: SortDirection;
}) {
  if (!active) {
    return (
      <svg
        className="h-3.5 w-3.5 opacity-40"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
        />
      </svg>
    );
  }
  if (direction === 'asc') {
    return (
      <svg
        className="h-3.5 w-3.5 text-brand"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    );
  }
  return (
    <svg
      className="h-3.5 w-3.5 text-brand"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

export function SortableTh({
  sortKey,
  sort,
  onSort,
  children,
  align = 'left',
  className,
  wrap = false,
}: SortableThProps) {
  const active = sort.key === sortKey;

  return (
    <th className={cn('px-3 py-2 font-medium', className)}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1',
          wrap ? 'w-full whitespace-normal text-left leading-tight' : 'whitespace-nowrap',
          align === 'right' && 'w-full justify-end',
          align === 'center' && 'w-full justify-center',
          active ? 'text-brand' : 'text-ink-muted hover:text-ink',
        )}
      >
        <span className={wrap ? 'min-w-0 flex-1' : undefined}>{children}</span>
        <SortIcon
          active={active}
          direction={active ? sort.direction : undefined}
        />
      </button>
    </th>
  );
}
